import { useTranslation } from 'react-i18next';
import type { DadosCartao } from '@/lib/picpay-sdk';

/**
 * Formulário de cartão.
 *
 * Location: src/components/checkout
 * Why: os dados digitados aqui **não** vão para a nossa API. Ficam no estado
 * deste componente até o SDK do PicPay convertê-los num token de uso único, e
 * só o token viaja. É o que mantém a Hystore fora do escopo PCI-DSS.
 *
 * Por isso três cuidados que parecem detalhe e não são:
 *
 * - `autoComplete="off"` no CVV. O navegador não deve guardar o código de
 *   segurança, mesmo que guarde o número.
 * - `inputMode="numeric"` em vez de `type="number"`, que come zeros à esquerda
 *   e mostra setas de incremento num cartão.
 * - Nada de `name` significativo em campo de cartão, para extensões e
 *   gerenciadores não capturarem o valor por engano.
 */

export interface CardFormProps {
  valor: DadosCartao;
  onChange: (v: DadosCartao) => void;
  parcelas: number;
  onParcelasChange: (n: number) => void;
  maxParcelas: number;
  desabilitado?: boolean;
}

const ANO_ATUAL = new Date().getFullYear();

export function CardForm({
  valor,
  onChange,
  parcelas,
  onParcelasChange,
  maxParcelas,
  desabilitado,
}: CardFormProps) {
  const { t } = useTranslation();

  const campo = (chave: keyof DadosCartao) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...valor, [chave]: e.target.value });

  const classe =
    'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-accent disabled:opacity-50';

  return (
    <div className="mt-4 space-y-3 rounded-lg border border-border bg-muted/30 p-4">
      <div className="space-y-1">
        <label htmlFor="cc-number" className="text-sm font-medium text-foreground">
          {t('store.cart.card_number')}
        </label>
        <input
          id="cc-number"
          inputMode="numeric"
          autoComplete="cc-number"
          maxLength={23}
          disabled={desabilitado}
          value={valor.numero}
          onChange={campo('numero')}
          placeholder="0000 0000 0000 0000"
          className={classe}
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="cc-name" className="text-sm font-medium text-foreground">
          {t('store.cart.card_holder')}
        </label>
        <input
          id="cc-name"
          autoComplete="cc-name"
          maxLength={60}
          disabled={desabilitado}
          value={valor.nome}
          onChange={campo('nome')}
          placeholder={t('store.cart.card_holder_placeholder')}
          className={classe}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <label htmlFor="cc-month" className="text-sm font-medium text-foreground">
            {t('store.cart.card_month')}
          </label>
          <input
            id="cc-month"
            inputMode="numeric"
            autoComplete="cc-exp-month"
            maxLength={2}
            disabled={desabilitado}
            value={valor.mesValidade}
            onChange={campo('mesValidade')}
            placeholder="MM"
            className={classe}
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="cc-year" className="text-sm font-medium text-foreground">
            {t('store.cart.card_year')}
          </label>
          <input
            id="cc-year"
            inputMode="numeric"
            autoComplete="cc-exp-year"
            maxLength={4}
            disabled={desabilitado}
            value={valor.anoValidade}
            onChange={campo('anoValidade')}
            placeholder={String(ANO_ATUAL)}
            className={classe}
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="cc-cvv" className="text-sm font-medium text-foreground">
            {t('store.cart.card_cvv')}
          </label>
          <input
            id="cc-cvv"
            inputMode="numeric"
            /* O navegador não deve guardar o código de segurança. */
            autoComplete="off"
            maxLength={4}
            disabled={desabilitado}
            value={valor.cvv}
            onChange={campo('cvv')}
            placeholder="000"
            className={classe}
          />
        </div>
      </div>

      {maxParcelas > 1 && (
        <div className="space-y-1">
          <label htmlFor="cc-inst" className="text-sm font-medium text-foreground">
            {t('store.cart.card_installments')}
          </label>
          <select
            id="cc-inst"
            disabled={desabilitado}
            value={parcelas}
            onChange={(e) => onParcelasChange(Number(e.target.value))}
            className={classe}
          >
            {Array.from({ length: maxParcelas }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}x
              </option>
            ))}
          </select>
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t('store.cart.card_security_note')}</p>
    </div>
  );
}
